import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Permission, UserRole } from "@/types";

// Pages
import LoginPage from "@/pages/login";
import ForgotPasswordPage from "@/pages/forgot-password";
import SetPasswordPage from "@/pages/set-password";
import DashboardPage from "@/pages/dashboard";
import SellerDetailPage from "@/pages/sellers/detail";
import ActivitiesPage from "@/pages/activities";
import ActivityDetailPage from "@/pages/activities/detail";
import BookingsPage from "@/pages/bookings";
import BookingDetailPage from "@/pages/bookings/BookingDetailPage";
import WalletPage from "@/pages/wallet";
import SettingsPage from "@/pages/settings";
import DevicesIndexRoute from "@/pages/devices/DevicesIndexRoute";
import AdminCarouselPage from "@/pages/admin/carousel";
import AdminDeviceStatsPage from "@/pages/admin/device-stats";
import ReadersPage from "@/pages/readers";
import HousesPage from "@/pages/houses";
import HouseDetailPage from "@/pages/houses/detail";
import SupportPage from "@/pages/support";
import TicketListPage from "@/pages/support/TicketListPage";
import TicketDetailPage from "@/pages/support/TicketDetailPage";
import AdminTicketsIndexRoute from "@/pages/admin/tickets/AdminTicketsIndexRoute";
import AdminChatsPage from "@/pages/admin/tickets/AdminChatsPage";
import AdminTicketDetailPage from "@/pages/admin/tickets/TicketDetailPage";
import AdminHostDetailPage from "@/pages/admin/hosts/detail";
import AdminCreateActivityPage from "@/pages/admin/create-activity";
import AdminCreateHousePage from "@/pages/admin/create-house";
import AdminSalesAgentDetailPage from "@/pages/admin/sales-agents/detail";
import AdminUsersPage from "@/pages/admin/users";
import AdminStaffAccessPage from "@/pages/admin/staff";
import AdminActivitiesPage from "@/pages/admin/activities";
import AdminStructuresPage from "@/pages/admin/structures";
import AdminPaymentsIndexRoute from "@/pages/admin/payments/AdminPaymentsIndexRoute";
import AdminPaymentsEventsListPage from "@/pages/admin/payments/EventsListPage";
import AdminPaymentsEventDetailPage from "@/pages/admin/payments/EventDetailPage";
import AdminPaymentsExternalPaymentsPage from "@/pages/admin/payments/ExternalPaymentsPage";
import SettlementsPayeesPage from "@/pages/admin/payments/SettlementsPayeesPage";
import SettlementPayeeDetailPage from "@/pages/admin/payments/SettlementPayeeDetailPage";
import AdminNotificationsPage from "@/pages/admin/notifications";
import AdminProtocolsPage from "@/pages/admin/protocols";
import AdminAgentTranscriptsPage from "@/pages/admin/agent-transcripts";
import CarouselSlideCreatePage from "@/pages/admin/carousel/CarouselSlideCreatePage";
import CarouselSlideEditPage from "@/pages/admin/carousel/CarouselSlideEditPage";
import AdminHandshakePage from "@/pages/admin/handshake";
import HandshakeAuditPage from "@/pages/admin/handshake/HandshakeAuditPage";
import PointsPage from "@/pages/points";
import SalesAgentAssociationsPage from "@/pages/sales-agent/associations";
import PrivacyPolicyPage from "@/pages/privacy-policy";
import { EnvironmentBanner } from "@/components/EnvironmentBanner";
import { IubendaScript } from "@/components/legal/IubendaScript";

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Layout>
        <Outlet />
      </Layout>
    </ProtectedRoute>
  );
}

function App() {
  const initAuth = useAuthStore((s) => s.initAuth);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <IubendaScript />
        <EnvironmentBanner />
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate
                  to="/"
                  replace
                />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate
                  to="/"
                  replace
                />
              ) : (
                <ForgotPasswordPage />
              )
            }
          />
          <Route
            path="/set-password"
            element={
              isAuthenticated ? (
                <Navigate
                  to="/"
                  replace
                />
              ) : (
                <SetPasswordPage />
              )
            }
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicyPage />}
          />

          <Route element={<ProtectedLayout />}>
            <Route
              index
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="activities"
              element={
                <ProtectedRoute
                  requirePermission={Permission.ACTIVITIES_UPDATE}
                >
                  <ActivitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="activities/:id"
              element={
                <ProtectedRoute
                  requirePermission={Permission.ACTIVITIES_UPDATE}
                >
                  <ActivityDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.BOOKINGS_READ,
                    Permission.BOOKINGS_READ_ANY,
                  ]}
                >
                  <BookingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="bookings/:bookingId"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.BOOKINGS_READ,
                    Permission.BOOKINGS_READ_ANY,
                  ]}
                >
                  <BookingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="points"
              element={
                <ProtectedRoute
                  requirePermission={Permission.CUSTOMER_POINTS_READ}
                >
                  <PointsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="wallet"
              element={
                <ProtectedRoute
                  requirePermission={Permission.WALLET_READ}
                  excludeRoles={[UserRole.ADMIN]}
                >
                  <WalletPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute
                  requirePermission={Permission.USERS_UPDATE_SELF}
                >
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="support"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SUPPORT_TICKETS_CREATE}
                >
                  <SupportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="support/tickets"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SUPPORT_TICKETS_READ_OWN}
                >
                  <TicketListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="support/tickets/:ticketId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SUPPORT_TICKETS_READ_OWN}
                >
                  <TicketDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="associations"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SALES_AGENT_ASSIGNMENTS_READ}
                >
                  <SalesAgentAssociationsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="houses"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.HOUSES_READ,
                    Permission.HOUSES_READ_ANY,
                  ]}
                >
                  <HousesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="houses/:houseId"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.HOUSES_READ,
                    Permission.HOUSES_READ_ANY,
                  ]}
                >
                  <HouseDetailPage />
                </ProtectedRoute>
              }
            />

            <Route path="devices" element={<DevicesIndexRoute />} />
            <Route
              path="devices/presets"
              element={<Navigate to="/devices?tab=presets" replace />}
            />
            <Route
              path="devices/carousel"
              element={
                <ProtectedRoute
                  requirePermission={Permission.CAROUSEL_SLIDES_READ}
                >
                  <AdminCarouselPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="devices/statistics"
              element={
                <ProtectedRoute requirePermission={Permission.STATS_READ}>
                  <AdminDeviceStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="devices/voice-transcripts"
              element={
                <ProtectedRoute
                  requirePermission={Permission.AGENT_VOICE_TRANSCRIPTS_READ}
                >
                  <AdminAgentTranscriptsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/device-stats"
              element={<Navigate to="/devices/statistics" replace />}
            />
            <Route
              path="admin/agent-transcripts"
              element={<Navigate to="/devices/voice-transcripts" replace />}
            />

            <Route
              path="readers"
              element={
                <ProtectedRoute requirePermission={Permission.READERS_READ}>
                  <ReadersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="sellers/:sellerId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SELLERS_READ_ANY}
                >
                  <SellerDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sellers/:sellerId/bookings"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.BOOKINGS_READ,
                    Permission.BOOKINGS_READ_ANY,
                  ]}
                >
                  <BookingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sellers/:sellerId/bookings/:bookingId"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.BOOKINGS_READ,
                    Permission.BOOKINGS_READ_ANY,
                  ]}
                >
                  <BookingDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="hosts/:hostId"
              element={
                <ProtectedRoute requirePermission={Permission.HOSTS_READ_ANY}>
                  <AdminHostDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/create-activity"
              element={
                <ProtectedRoute
                  requirePermission={Permission.ACTIVITIES_CREATE}
                >
                  <AdminCreateActivityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/create-house"
              element={
                <ProtectedRoute requirePermission={Permission.HOUSES_CREATE}>
                  <AdminCreateHousePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="admin/users"
              element={
                <ProtectedRoute
                  requirePermission={Permission.USERS_READ_ANY}
                >
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/staff/:userId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.STAFF_PERMISSIONS_UPDATE}
                >
                  <AdminStaffAccessPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/activities"
              element={
                <ProtectedRoute
                  requirePermission={Permission.ACTIVITIES_MANAGE_ANY}
                >
                  <AdminActivitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/structures"
              element={
                <ProtectedRoute
                  requirePermission={Permission.HOUSES_READ_ANY}
                >
                  <AdminStructuresPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/tickets"
              element={<AdminTicketsIndexRoute />}
            />
            <Route
              path="admin/chats"
              element={
                <ProtectedRoute
                  requirePermission={Permission.CUSTOMER_SUPPORT_CHATS_READ_ANY}
                >
                  <AdminChatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/notifications"
              element={
                <ProtectedRoute
                  requirePermission={Permission.NOTIFICATIONS_READ}
                >
                  <AdminNotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/protocols"
              element={
                <ProtectedRoute
                  requirePermission={Permission.PROTOCOLS_READ}
                >
                  <AdminProtocolsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/carousel"
              element={<Navigate to="/devices/carousel" replace />}
            />
            <Route
              path="admin/carousel/new"
              element={
                <ProtectedRoute
                  requirePermission={Permission.CAROUSEL_SLIDES_WRITE}
                >
                  <CarouselSlideCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/carousel/:slideId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.CAROUSEL_SLIDES_READ}
                >
                  <CarouselSlideEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/tickets/:ticketId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SUPPORT_TICKETS_READ_ANY}
                >
                  <AdminTicketDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/handshake"
              element={
                <ProtectedRoute
                  requirePermission={Permission.HANDSHAKES_SESSIONS_READ_ANY}
                >
                  <AdminHandshakePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/handshake/:sessionId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.HANDSHAKES_SESSIONS_READ_ANY}
                >
                  <HandshakeAuditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/sales-agents/:salesAgentId"
              element={
                <ProtectedRoute
                  requirePermission={Permission.SALES_AGENTS_READ_ANY}
                >
                  <AdminSalesAgentDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="admin/payments"
              element={
                <ProtectedRoute
                  requirePermission={[
                    Permission.ECONOMIC_EVENTS_LIST,
                    Permission.EXTERNAL_PAYMENTS_LIST,
                    Permission.SETTLEMENTS_LIST,
                  ]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminPaymentsIndexRoute />} />
              <Route
                path="events"
                element={
                  <ProtectedRoute
                    requirePermission={Permission.ECONOMIC_EVENTS_LIST}
                  >
                    <AdminPaymentsEventsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="events/:eventId"
                element={
                  <ProtectedRoute
                    requirePermission={Permission.ECONOMIC_EVENTS_LIST}
                  >
                    <AdminPaymentsEventDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="external-payments"
                element={
                  <ProtectedRoute
                    requirePermission={Permission.EXTERNAL_PAYMENTS_LIST}
                  >
                    <AdminPaymentsExternalPaymentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settlements"
                element={
                  <ProtectedRoute
                    requirePermission={Permission.SETTLEMENTS_LIST}
                  >
                    <SettlementsPayeesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settlements/:userId"
                element={
                  <ProtectedRoute
                    requirePermission={Permission.SETTLEMENTS_LIST}
                  >
                    <SettlementPayeeDetailPage />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Route>

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "var(--color-surface-0)",
            color: "var(--color-surface-900)",
            border: "1px solid var(--color-surface-200)",
            boxShadow: "var(--shadow-popover)",
            borderRadius: "10px",
            padding: "10px 14px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "var(--color-success-500)",
              secondary: "var(--color-surface-0)",
            },
          },
          error: {
            iconTheme: {
              primary: "var(--color-error-500)",
              secondary: "var(--color-surface-0)",
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
